import { Container, Title, Text, Stack, Button, Group, Accordion, Image, Center } from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'

export function AddToHomeScreenPage() {
    const navigate = useNavigate()

    return (
        <Container size="md" py="xl">
            <Stack gap="xl">
                <Group gap="md">
                    <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={() => navigate('/help')}
                    >
                        Back to Help
                    </Button>
                </Group>

                <div>
                    <Title order={1} size="h1" mb="md">Add Spotter to Your Home Screen</Title>
                    <Text size="lg" c="dimmed">
                        Get the best Spotter experience by installing the app on your device.
                        It works just like a native app but runs in your browser.
                    </Text>
                </div>

                <Accordion variant="separated" radius="md" defaultValue="ios-safari">
                    <Accordion.Item value="ios-safari">
                        <Accordion.Control>
                            <Title order={3} size="h4">📱 iPhone & iPad (Safari)</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="md">
                                <Text>Follow these steps to add Spotter to your home screen on iOS:</Text>

                                <Stack gap="sm">
                                    <Text><strong>Step 1:</strong> Open Spotter in Safari (not Chrome or other browsers)</Text>
                                    <Text><strong>Step 2:</strong> Tap the Share button at the bottom of the screen</Text>
                                    <Center>
                                        <Image
                                            src="/images/ios-share-button.png"
                                            alt="iOS Safari Share Button"
                                            w={200}
                                            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Crect width='200' height='100' fill='%23f0f0f0'/%3E%3Ctext x='100' y='50' text-anchor='middle' dy='.3em' fill='%23666'%3EShare Button Image%3C/text%3E%3C/svg%3E"
                                        />
                                    </Center>
                                    <Text><strong>Step 3:</strong> Scroll down and tap "Add to Home Screen"</Text>
                                    <Text><strong>Step 4:</strong> Tap "Add" in the top-right corner</Text>
                                    <Text><strong>Step 5:</strong> Spotter will now appear on your home screen like a regular app!</Text>
                                </Stack>

                                <Text size="sm" c="dimmed">
                                    <strong>Note:</strong> This only works in Safari. If you're using Chrome or another browser,
                                    you'll need to open Spotter in Safari first.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="android-chrome">
                        <Accordion.Control>
                            <Title order={3} size="h4">🤖 Android (Chrome)</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="md">
                                <Text>Follow these steps to install Spotter on Android:</Text>

                                <Stack gap="sm">
                                    <Text><strong>Step 1:</strong> Open Spotter in Chrome</Text>
                                    <Text><strong>Step 2:</strong> Look for the "Install" prompt at the top of the screen</Text>
                                    <Text><strong>Step 3:</strong> Tap "Install" when prompted</Text>
                                    <Text><strong>Alternative:</strong> Tap the three dots menu → "Add to Home screen"</Text>
                                    <Text><strong>Step 4:</strong> Confirm by tapping "Add"</Text>
                                    <Text><strong>Step 5:</strong> Spotter will be installed as an app on your device!</Text>
                                </Stack>

                                <Text size="sm" c="dimmed">
                                    <strong>Note:</strong> The install prompt may appear automatically when you visit Spotter.
                                    If you dismissed it, you can find the option in the Chrome menu.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="desktop-chrome">
                        <Accordion.Control>
                            <Title order={3} size="h4">💻 Desktop (Chrome, Edge, etc.)</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="md">
                                <Text>Install Spotter on your desktop for quick access:</Text>

                                <Stack gap="sm">
                                    <Text><strong>Step 1:</strong> Open Spotter in Chrome, Edge, or another Chromium-based browser</Text>
                                    <Text><strong>Step 2:</strong> Look for the install icon in the address bar (usually a computer with a down arrow)</Text>
                                    <Text><strong>Step 3:</strong> Click the install icon and then "Install"</Text>
                                    <Text><strong>Alternative:</strong> Click the three dots menu → "Install Spotter..."</Text>
                                    <Text><strong>Step 4:</strong> Spotter will open in its own window and appear in your applications!</Text>
                                </Stack>

                                <Text size="sm" c="dimmed">
                                    <strong>Benefits:</strong> The installed app opens in its own window, appears in your dock/taskbar,
                                    and works offline for basic functionality.
                                </Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="benefits">
                        <Accordion.Control>
                            <Title order={3} size="h4">✨ Benefits of Installing</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text><strong>Faster Access:</strong> Launch Spotter directly from your home screen or applications folder</Text>
                                <Text><strong>Full Screen:</strong> No browser address bar or tabs - just the app</Text>
                                <Text><strong>Offline Support:</strong> View previously loaded spottings even without internet</Text>
                                <Text><strong>Better Performance:</strong> Optimized for app-like experience</Text>
                                <Text><strong>Push Notifications:</strong> Get notified about new spottings in your area (coming soon)</Text>
                                <Text><strong>Native Feel:</strong> Behaves just like a regular app on your device</Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="troubleshooting">
                        <Accordion.Control>
                            <Title order={3} size="h4">🔧 Troubleshooting</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="sm">
                                <Text><strong>Don't see the install option?</strong></Text>
                                <Text pl="md">• Make sure you're using a supported browser (Safari on iOS, Chrome on Android/Desktop)</Text>
                                <Text pl="md">• Try refreshing the page</Text>
                                <Text pl="md">• Check if you've already installed it</Text>

                                <Text><strong>Installation failed?</strong></Text>
                                <Text pl="md">• Make sure you have enough storage space</Text>
                                <Text pl="md">• Try closing other browser tabs</Text>
                                <Text pl="md">• Restart your browser and try again</Text>

                                <Text><strong>Want to uninstall?</strong></Text>
                                <Text pl="md">• iOS: Long press the app icon → "Remove App"</Text>
                                <Text pl="md">• Android: Long press the app icon → "Uninstall"</Text>
                                <Text pl="md">• Desktop: Right-click the app → "Uninstall" or use your system's app manager</Text>
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </Stack>
        </Container>
    )
} 